import { create } from 'zustand';
import { api, setAuthToken } from './api';

let loadPromise = null;   // dedupes concurrent loads
let lastLoad = 0;         // throttle window
const MIN_INTERVAL = 2500;

export const useAuth = create((set, get) => ({
  token: localStorage.getItem('wof_token') || null,
  user: null,
  hero: null,
  city: null,
  country: null,
  guild: null,
  shops: [],
  unread: 0,
  pendingGifts: 0,
  loading: true,

  async setToken(token) {
    localStorage.setItem('wof_token', token);
    setAuthToken(token);
    set({ token });
    lastLoad = 0;                 // force a fresh load
    await get().load(true);
  },

  /**
   * Loads /auth/me.
   * · dedupes parallel calls
   * · throttles to 1 request / 2.5s unless force=true
   */
  async load(force = false) {
    const { token } = get();
    if (!token) { set({ loading: false }); return null; }

    if (loadPromise) return loadPromise;

    const now = Date.now();
    if (!force && now - lastLoad < MIN_INTERVAL) return null;

    lastLoad = now;

    loadPromise = (async () => {
      try {
        const { data } = await api.get('/auth/me');
        set({
          user: data.user,
          hero: data.hero,
          city: data.city,
          country: data.country,
          guild: data.guild,
          shops: data.shops || [],
          unread: data.unreadNotifications || 0,
          pendingGifts: data.pendingGifts || 0,
          loading: false,
        });
        return data;
      } catch (e) {
        // never log out on rate limit / network hiccup
        if (e?.status === 401 || e?.status === 403) {
          get().logout();
        } else {
          set({ loading: false });
        }
        return null;
      } finally {
        loadPromise = null;
      }
    })();

    return loadPromise;
  },

  /** Cheap local updates — no network */
  patchUser(patch) {
    set(s => ({ user: s.user ? { ...s.user, ...patch } : s.user }));
  },
  addPoints(n) {
    set(s => s.user
      ? { user: { ...s.user, points_balance: (s.user.points_balance || 0) + n } }
      : {});
  },
  setUnread(n) { set({ unread: n }); },
  setPendingGifts(n) { set({ pendingGifts: n }); },

  logout() {
    localStorage.removeItem('wof_token');
    setAuthToken(null);
    lastLoad = 0;
    loadPromise = null;
    set({
      token: null, user: null, hero: null, city: null, country: null,
      guild: null, shops: [], unread: 0, pendingGifts: 0, loading: false,
    });
  },
}));

export default useAuth;